import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import SidePanel from 'common/side-panel/side-panel';
import Spinner from 'common/spinner/components/spinner';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { useNavigate, useParams } from 'react-router-dom';
import { Col, Form } from 'antd';
import { FormField, Input, Button, Row } from 'ui';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { addWipCode, fetchWipCode, updateWipCode }
  from '../../containers/wip-codes/wip-codes-actions';

const StyledButton = styled(Button)`
  text-transform: uppercase;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
`;

const Buttons = styled(Row)`
  gap: 20px;
`;

const WipCodeSidepanel = ({ intl, scope }) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const {
    wipCode,
    isLoading,
  } = useSelector(state => state.wipCodes);

  const close = () => {
    navigate(-1);
  };

  const handleSave = () => {
    if (params?.id) {
      dispatch(updateWipCode({ scope, id: params?.id, ...form.getFieldsValue() }));
    } else {
      dispatch(addWipCode({ scope, ...form.getFieldsValue() }));
    }
    close();
  };

  useEffect(() => {
    if (params.id) {
      dispatch(fetchWipCode(params.id));
    }
  }, [params.id]);

  useDeepCompareEffect(() => {
    if (wipCode) {
      form.setFieldsValue({
        action: wipCode?.action,
        code: wipCode?.code,
        description: wipCode?.description,
      });
    }
  }, [wipCode]);

  return (
    <SidePanel
      width={'40%'}
      title={intl.formatMessage({
        id: params?.id ? 'wipCodes.EDIT_WIP_CODE' : 'wipCodes.ADD_WIP_CODE',
      })}
      onPanelClose={close}
      contentOverflow={false}
    >
      {isLoading ?
        <Spinner /> :
        <Form
          layout='vertical'
          onFinish={handleSave}
          requiredMark={true}
          form={form}
        >
          <Row>
            <Col span={10}>
              <FormField
                label={intl.formatMessage({ id: 'wipCodes.CODE' })}
                name='code'
                rules={[{
                  required: true,
                  message: intl.formatMessage({ id: 'wipCodes.errors.codeRequired' }),
                }]}
              >
                <Input disabled={isLoading} />
              </FormField>
            </Col>
          </Row>
          <Row>
            <Col span={10}>
              <FormField
                label={intl.formatMessage({ id: 'wipCodes.DESCRIPTION' })}
                name='description'
                rules={[{
                  required: true,
                  message: intl.formatMessage({ id: 'wipCodes.errors.descriptionRequired' }),
                }]}
              >
                <Input disabled={isLoading} />
              </FormField>
            </Col>
          </Row>
          <Row>
            <Col span={10}>
              <FormField
                label={intl.formatMessage({ id: 'wipCodes.ACTION' })}
                name='action'
                rules={[{
                  required: false,
                }]}
              >
                <Input disabled={isLoading} />
              </FormField>
            </Col>
          </Row>
          <Buttons marginTop={20}>
            <StyledButton onClick={close} disabled={isLoading}>
              {intl.formatMessage({ id: 'common.CANCEL' })}
            </StyledButton>
            <StyledButton
              htmlType='submit'
              type='secondary'
              disabled={isLoading}
            >
              {intl.formatMessage({
                id: params?.id ? 'wipCodes.EDIT_WIP_CODE' : 'wipCodes.ADD_WIP_CODE',
              })}
            </StyledButton>
          </Buttons>
        </Form>
      }
    </SidePanel>
  );
};

WipCodeSidepanel.propTypes = {
  intl: PropTypes.object,
  scope: PropTypes.string,
};

export default injectIntl(WipCodeSidepanel);
